

import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase-client';
import OnboardingModal from '@/components/OnboardingModal';

export default function SignUpPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const { signUp, signInWithGoogle } = useAuth();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [userId, setUserId] = useState('');
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [needsEmailConfirmation, setNeedsEmailConfirmation] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      console.log('[SignUp] Submitting form with:', formData.email);
      const { data, error: signUpError } = await signUp(formData.email, formData.password, {
        full_name: formData.fullName,
      });

      console.log('[SignUp] Result:', {
        hasData: !!data,
        hasUser: !!data?.user,
        userId: data?.user?.id,
        error: signUpError?.message
      });

      if (signUpError) {
        console.error('[SignUp] Error:', signUpError);
        throw signUpError;
      }

      if (data?.user) {
        console.log('[SignUp] User created successfully');
        setUserId(data.user.id);
        setUserEmail(formData.email);

        if (!data.session) {
          console.log('[SignUp] No session - email confirmation will be required after onboarding');
          setNeedsEmailConfirmation(true);
        }

        setShowOnboarding(true);
      } else {
        console.error('[SignUp] No user in response data');
        setError('Failed to create user account');
      }
    } catch (err: any) {
      console.error('[SignUp] Exception:', err);
      setError(err.message || 'An error occurred during sign up');
    } finally {
      setLoading(false);
    }
  };

  const handleOnboardingComplete = async (onboardingData: any) => {
    setOnboardingLoading(true);
    try {
      let profilePhotoUrl = null;
      let resumePath = null;
      let resumeFilename = null;

      if (!needsEmailConfirmation) {
        const { uploadFile } = await import('@/lib/edge-functions');

        if (onboardingData.profilePhoto) {
          try {
            const photoData = await uploadFile(onboardingData.profilePhoto, 'avatars', userId);
            profilePhotoUrl = photoData.path;
          } catch (error) {
            console.error('Photo upload failed:', error);
          }
        }

        if (onboardingData.resume) {
          try {
            const resumeData = await uploadFile(onboardingData.resume, 'resumes', userId);
            resumePath = resumeData.path;
            resumeFilename = resumeData.originalFilename;
          } catch (error) {
            console.error('Resume upload failed:', error);
          }
        }
      } else {
        console.log('[SignUp] Skipping file uploads - will be available after email confirmation');
      }

      // Save onboarding data to user_profiles (only bio if email confirmation needed)
      const updateData: any = {
        bio: onboardingData.bio,
        updated_at: new Date().toISOString(),
      };

      if (!needsEmailConfirmation) {
        updateData.profile_photo_url = profilePhotoUrl;
        updateData.resume_url = resumePath;
        updateData.resume_filename = resumeFilename;
      }

      const { error: profileError } = await supabase
        .from('user_profiles')
        .update(updateData)
        .eq('id', userId);

      if (profileError) {
        console.error('Profile update error:', profileError);
        setError('Failed to save onboarding data');
        setOnboardingLoading(false);
        return;
      }

      setOnboardingLoading(false);
      setShowOnboarding(false);

      if (needsEmailConfirmation) {
        console.log('[SignUp] Onboarding complete, showing email confirmation screen');
        setSignupSuccess(true);
      } else {
        console.log('[SignUp] Onboarding complete, redirecting to', returnTo || '/dashboard');
        navigate(returnTo || '/dashboard');
      }
    } catch (err: any) {
      console.error('Error saving onboarding:', err);
      setError(err.message || 'Failed to complete onboarding');
      setOnboardingLoading(false);
    }
  };

  const handleSkipOnboarding = () => {
    if (needsEmailConfirmation) {
      setShowOnboarding(false);
      setSignupSuccess(true);
    } else {
      navigate(returnTo || '/dashboard');
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError('');

    const redirectUrl = returnTo
      ? `${window.location.origin}${returnTo}`
      : `${window.location.origin}/`;

    const { error: signInError } = await signInWithGoogle(redirectUrl);

    if (signInError) {
      setError(signInError.message || 'An error occurred during Google sign in');
      setGoogleLoading(false);
    }
  };

  if (signupSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold text-slate-900 mb-2">Check your email</h2>
          <p className="text-slate-600 mb-6">
            We've sent a confirmation link to <strong>{userEmail}</strong>
          </p>
          <p className="text-sm text-slate-500 mb-6">
            Click the link in the email to verify your account. The email is from <strong>Teech Interviews</strong> (via Supabase).
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-left text-sm text-amber-800 mb-4">
            <p className="font-medium mb-1">Note about file uploads:</p>
            <p>Your CV and profile photo will be available to upload after you confirm your email and sign in to your profile.</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-left text-sm text-slate-600">
            <p className="font-medium text-slate-700 mb-2">After confirming:</p>
            <ol className="list-decimal list-inside space-y-1 ml-2">
              <li>Click the confirmation link in your email</li>
              <li>You'll be redirected back to this site</li>
              <li>Sign in with your email and password</li>
              <li>Go to your Profile to upload your CV</li>
            </ol>
          </div>
          <a
            href="/login"
            className="mt-6 inline-block text-sm text-emerald-600 hover:text-emerald-700 font-medium"
          >
            Go to sign in page
          </a>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen grid md:grid-cols-2">
        {/* Left: Form */}
        <div className="flex flex-col justify-between px-6 py-8 md:px-16 md:py-10 bg-white">
          {/* Brand */}
          <div className="flex items-center justify-center">
            <img
              src="https://suqzclerofxnjwpmnbdy.supabase.co/storage/v1/object/public/Site%20Images/png%20logo.png"
              alt="Interview Trainer"
              className="h-20 w-auto"
            />
          </div>

          {/* Sign up */}
          <div className="max-w-md mx-auto w-full mt-10 mb-12">
            <h1 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-3">
              Create your account
            </h1>
            <p className="text-slate-500 mb-8">
              Get tailored mock interviews, scoring and feedback based on your goals.
            </p>

            {error && (
              <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-600">
                {error}
              </div>
            )}

            {/* Google Sign In */}
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={loading || googleLoading}
              className="w-full mb-4 rounded-full border border-slate-200 bg-white text-slate-700 py-2.5 text-sm font-medium shadow-sm hover:bg-slate-50 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {googleLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-700"></div>
              ) : (
                <svg className="h-4 w-4" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
              )}
              Continue with Google
            </button>

            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-400">Or continue with email</span>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Full name
                </label>
                <input
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full rounded-full border border-slate-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald-500/70 focus:border-emerald-500 bg-slate-50"
                  placeholder="Alex Johnson"
                  disabled={loading || googleLoading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Email address
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full rounded-full border border-slate-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald-500/70 focus:border-emerald-500 bg-slate-50"
                  placeholder="you@example.com"
                  disabled={loading || googleLoading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={6}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full rounded-full border border-slate-200 px-4 py-2.5 pr-10 text-sm outline-none focus:ring-2 focus:ring-emerald-500/70 focus:border-emerald-500 bg-slate-50"
                    placeholder="Create a secure password"
                    disabled={loading || googleLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Confirm password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    minLength={6}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className={`w-full rounded-full border px-4 py-2.5 pr-10 text-sm outline-none focus:ring-2 focus:ring-emerald-500/70 focus:border-emerald-500 bg-slate-50 ${
                      formData.confirmPassword && formData.password !== formData.confirmPassword
                        ? 'border-red-400'
                        : 'border-slate-200'
                    }`}
                    placeholder="Repeat your password"
                    disabled={loading || googleLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                  <p className="mt-1 text-xs text-red-500 pl-4">Passwords do not match</p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || googleLoading || (!!formData.confirmPassword && formData.password !== formData.confirmPassword)}
                className="w-full mt-4 rounded-full bg-emerald-600 text-white py-2.5 text-sm font-medium shadow-sm hover:bg-emerald-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating account...' : 'Sign up'}
              </button>
            </form>

            <p className="mt-6 text-xs text-slate-400">
              By signing up you agree to our Terms and Privacy Policy.
            </p>
          </div>

          <div className="space-y-2 text-xs text-slate-400 text-center md:text-left">
            <p>
              Already have an account?{' '}
              <a href={`/login${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`} className="text-emerald-600 hover:underline">
                Sign in
              </a>
            </p>
            <p>
              Are you a recruiter?{' '}
              <a href="/signup/recruiter" className="text-emerald-600 hover:underline">
                Sign up here
              </a>
            </p>
          </div>
        </div>

        {/* Right: Image / gradient */}
        <div className="hidden md:block relative">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 via-green-400 to-teal-300 opacity-90"></div>
          <div className="absolute inset-8 rounded-3xl border border-white/40 backdrop-blur-sm bg-white/10 flex flex-col justify-between p-8">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-white/70 mb-4">
                Tailored AI coaching
              </p>
              <h2 className="text-3xl font-semibold text-white mb-3">
                Get interview-ready in days, not months.
              </h2>
              <p className="text-sm text-white/80 max-w-sm mb-6">
                Practice with realistic interview scenarios, get content and behaviour scores,
                and track your progress over time.
              </p>

              {/* Preview Image */}
              <div className="rounded-2xl overflow-hidden shadow-2xl border-2 border-white/30">
                <img
                  src="https://suqzclerofxnjwpmnbdy.supabase.co/storage/v1/object/public/Site%20Images/Interview%20page.jpg"
                  alt="Interview dashboard preview"
                  className="w-full h-auto"
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-3 text-white/90">
                <div className="w-1.5 h-1.5 rounded-full bg-white/80 mt-2 flex-shrink-0"></div>
                <span className="text-sm">Personalized mock interviews based on your career goals</span>
              </div>
              <div className="flex items-start gap-3 text-white/90">
                <div className="w-1.5 h-1.5 rounded-full bg-white/80 mt-2 flex-shrink-0"></div>
                <span className="text-sm">Real-time AI feedback on your answers and body language</span>
              </div>
              <div className="flex items-start gap-3 text-white/90">
                <div className="w-1.5 h-1.5 rounded-full bg-white/80 mt-2 flex-shrink-0"></div>
                <span className="text-sm">Detailed analytics showing your improvement areas</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showOnboarding && (
        <>
          <OnboardingModal
            isOpen={showOnboarding}
            onComplete={handleOnboardingComplete}
            onSkip={handleSkipOnboarding}
            needsEmailConfirmation={needsEmailConfirmation}
          />
          {onboardingLoading && (
            <div className="fixed inset-0 bg-slate-900/80 flex items-center justify-center z-[60]">
              <div className="bg-white rounded-2xl p-8 max-w-sm mx-4 text-center shadow-2xl">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Setting up your profile...</h3>
                <p className="text-sm text-slate-600">Uploading your files and saving your preferences</p>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
